/**
 * Crafting recipes. Each input carries a human "source" string so the crafting
 * UI can tell the player where/how to obtain the material.
 */
export interface RecipeInput {
  kind: "minerals" | "units" | "item";
  id?: string; // for kind "item" (e.g. "moonCore")
  amount: number;
  label: string;
  source: string; // where/how to get it
}

export interface Recipe {
  id: string;
  name: string;
  icon: string;
  desc: string; // what it's used for
  inputs: RecipeInput[];
  outputItem: string;
  outputQty: number;
}

export const RECIPES: Recipe[] = [
  {
    id: "energyCore",
    name: "Energy Core",
    icon: "⬢",
    desc: "A dense power cell. Required to refit the mothership to Mk II and Mk III at a mechanical planet.",
    inputs: [
      {
        kind: "units",
        amount: 3,
        label: "Universal Units",
        source:
          "Trade minerals at a trade-hub planet (🛒) — 100 minerals = 1 Unit.",
      },
      {
        kind: "item",
        id: "moonCore",
        amount: 2,
        label: "Moon Core",
        source: "Destroy moons (10% drop) or sub-moons (5% drop).",
      },
    ],
    outputItem: "energyCore",
    outputQty: 1,
  },
  {
    id: "warpFuel",
    name: "Warp Drive Fuel",
    icon: "⛽",
    desc: "Single-use fuel cell. Consumed to warp (Warp ▸) to a brand-new solar system.",
    inputs: [
      {
        kind: "item",
        id: "darkMatter",
        amount: 10,
        label: "Dark Matter",
        source: "5% drop from destroying any asteroid.",
      },
      {
        kind: "units",
        amount: 100,
        label: "Universal Units",
        source:
          "Trade minerals at a trade-hub planet (🛒) — 100 minerals = 1 Unit.",
      },
    ],
    outputItem: "warpFuel",
    outputQty: 1,
  },
  {
    id: "turret_basic",
    name: "Twin Cannon",
    icon: "🔫",
    desc: "A twin-barrel laser turret. Install it on a Mk II+ hull mount in the Arsenal.",
    inputs: [
      {
        kind: "minerals",
        amount: 1500,
        label: "Minerals",
        source: "Mine asteroids, moons, and planets.",
      },
      {
        kind: "units",
        amount: 15,
        label: "Universal Units",
        source: "Trade minerals at a trade-hub planet (100:1).",
      },
    ],
    outputItem: "turret_basic",
    outputQty: 1,
  },
  {
    id: "cannon_big",
    name: "Big Cannon",
    icon: "🔆",
    desc: "A Mk V Battleship super-gun. Install it in a Big Cannon slot (centre spine) in the Arsenal — fires a beam that reaches clean across the field.",
    inputs: [
      {
        kind: "item",
        id: "darkMatter",
        amount: 5,
        label: "Dark Matter",
        source: "5% drop from destroying any asteroid.",
      },
      {
        kind: "units",
        amount: 500,
        label: "Universal Units",
        source: "Trade minerals at a trade-hub planet (100:1).",
      },
    ],
    outputItem: "cannon_big",
    outputQty: 1,
  },
];

export const RECIPES_BY_ID: Record<string, Recipe> = Object.fromEntries(
  RECIPES.map((r) => [r.id, r]),
);
